<template>
  <div class="paginated-items-container">
    <client-only>
      <div class="head">
        <h2 class="title">{{ title }}</h2>
        <div class="filters">
          <span
            :title="sortSensTitle"
            class="sorting-indicator u-cursor-pointer u-mb-xl"
            @click="
              handleSort(sorting.field[0] === '-' ? sorting.field.slice(1) : sorting.field, sorting.name)
            "
          >
            <uikit-arrow-up-and-down-icon class="arrow-icon" :type="sortSens" />
          </span>

          <uikit-dropdown ref="sortingController" :forceCloseOnClick="true" class="u-mb-xl">
            <uikit-button type="link">
              {{ sorting.name }}
              <template #right-icon>
                <uikit-chevron-icon type="down" />
              </template>
            </uikit-button>
            <template #overlay>
              <uikit-menu>
                <uikit-menu-item
                  id="sort-price"
                  :class="{ active: sorting.field === 'prix' || sorting.field === '-prix' }"
                  @click="handleSort('prix', 'Prix')"
                >
                  Prix
                </uikit-menu-item>
                <uikit-menu-item
                  id="sort-price-square-meter"
                  :class="{ active: sorting.field === 'prixm2' || sorting.field === '-prixm2' }"
                  @click="handleSort('prixm2', 'Prix au m2')"
                >
                  Prix au m2
                </uikit-menu-item>
                <uikit-menu-item
                  id="sort-performance"
                  :class="{ active: sorting.field === 'rendement' || sorting.field === '-rendement' }"
                  @click="handleSort('rendement', 'Rendement brut')"
                >
                  Rendement brut
                </uikit-menu-item>
                <uikit-menu-item
                  id="sort-surface"
                  :class="{ active: sorting.field === 'surface' || sorting.field === '-surface' }"
                  @click="handleSort('surface', 'Surface')"
                >
                  Surface
                </uikit-menu-item>
                <uikit-menu-item
                  id="sort-date"
                  :class="{ active: sorting.field === 'creation_date' || sorting.field === '-creation_date' }"
                  @click="handleSort('creation_date', 'Date de publication')"
                >
                  Date de publication
                </uikit-menu-item>
              </uikit-menu>
            </template>
          </uikit-dropdown>
        </div>
      </div>

      <div v-if="displayEmptyState" class="empty-state">
        {{ emptyStateText }}
      </div>
      <div v-else class="items">
        <template v-for="ad in adsToDisplay" :key="ad.id">
          <AnnonceCard class="ad-card-col" :ad="ad" :skeleton="displaySkeleton" />
        </template>
      </div>
      <div class="pagination">
        <uikit-pagination :current="currentPage" :pages="pages" @on-change="pageChangeHandler" />
      </div>
    </client-only>
  </div>
</template>

<script setup>
import {
  useArrayDatasource,
  UikitPagination,
  useScreenSizeStore,
  UikitChevronIcon,
  UikitMenuItem,
  UikitMenu,
  UikitArrowUpAndDownIcon,
  UikitDropdown,
  UikitButton,
} from '@hz/ui-kit'
import { useTubApi } from '../composables/tub-api'

const props = defineProps({
  criteria: {
    type: Object,
    required: false,
  },
  desktopPageSize: {
    type: Number,
    default: 9,
  },
  mobileSize: {
    type: Number,
    default: 3,
  },
  title: {
    type: String,
    default: '',
  },
  emptyStateText: {
    type: String,
    default: 'Votre agence ne propose pas d’annonce pour le moment.',
  },
})

const sortingController = ref(null)
const currentPage = ref(1)
const sorting = ref({
  field: '-rendement',
  name: 'Rendement brut',
})
const sortSens = computed(() => {
  if (sorting.value.field.startsWith('-')) {
    return 'down'
  }

  return 'up'
})
const sortSensTitle = computed(() => {
  if (sorting.value.field.startsWith('-')) {
    return 'ordre décroissant'
  }

  return 'Ordre croissant '
})
const displaySkeleton = ref(true)
const isMobile = computed(() => useScreenSizeStore().isMobileWidth)
const pageSize = computed(() => (isMobile.value ? props.mobileSize : props.desktopPageSize))

watch(
  () => pageSize.value,
  () => {
    datasource.setPageSize(pageSize.value)
    datasource.load()
  },
)

const datasource = useArrayDatasource([])

const { data: ads, pages } = datasource.getDataAndMetas()

const adsToDisplay = computed(() => {
  if (displaySkeleton.value) {
    return Array.from({ length: pageSize.value }, (i) => ({ id: `skeleton-${i}` }))
  }

  return ads.value
})

const displayEmptyState = computed(() => {
  return !displaySkeleton.value && !adsToDisplay.value.length
})

const pageChangeHandler = (page) => {
  currentPage.value = page
  datasource.setPage(page)
  datasource.load()
}

const handleSort = async (field, name) => {
  sorting.value.name = name
  if (sorting.value.field === field) {
    sorting.value.field = `-${field}`
  } else if (sorting.value.field === `-${field}`) {
    sorting.value.field = field
  } else {
    sorting.value.field = field
  }
  sortingController.value.hide()
  await refresh()
}

const refresh = async () => {
  displaySkeleton.value = true
  const params = {
    sort: sorting.value.field,
    filter: {
      ...props.criteria,
      ignoreAdminRole: true,
    },
  }

  const adsResponse = await useTubApi().find({
    ...params,
    filter: JSON.stringify(params.filter),
  })

  datasource.setData(adsResponse.data)
  datasource.setPageSize(pageSize.value)
  datasource.setPage(currentPage.value)
  datasource.load()
  displaySkeleton.value = false
}

onMounted(async () => {
  await refresh()
})
</script>

<style lang="less" scoped>
:deep(.menu-item.active) {
  color: var(--ds-color-primary-100);
}

.filters {
  display: flex;
  align-items: center;
  gap: 8px;
}

.paginated-items-container {
  .empty-state {
    color: var(--ds-color-gray-50);
    font-size: 16px;
    font-style: normal;
    font-weight: var(--ds-weight-regular);
  }
  .items {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 12px;
  }
}

:deep(.real-estate-card) {
  width: 100%;
}

@media @bp-lg {
  .paginated-items-container {
    .items {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      flex-wrap: wrap;
      width: 100%;
      gap: 32px;
    }

    .head {
      display: flex;
      justify-content: space-between;
    }
  }

  :deep(.real-estate-card) {
    width: 224px;
  }
}
</style>
